// src/components/table/Table.tsx
// Rediseño corporativo estilo Notion/Linear
import { useEffect, useState, useCallback, useContext } from "react";
import { Box, Typography, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, CircularProgress, alpha } from "@mui/material";
import { Receipt, Person, CalendarToday, Payment, Refresh, Visibility, CheckCircleOutline, Schedule, CancelOutlined } from "@mui/icons-material";
import { TransactionsServiceApi } from "../../services/api/transactionsServiceApi";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { TransactionDetailModal } from "./TransactionDetailModal";
import { DarkModeContext } from "../../context/darkModeContext";

const P = {
  light: { bg: "#FFFFFF", bgS: "#F8FAFC", bgT: "#F1F5F9", text: "#0F172A", textS: "#475569", textT: "#64748B", border: "#E2E8F0", accent: "#0369A1", accentBg: "#E0F2FE", success: "#15803D", successBg: "#DCFCE7", warning: "#B45309", warningBg: "#FEF3C7", error: "#DC2626", errorBg: "#FEE2E2", shadow: "0 1px 2px rgba(15,23,42,0.06)", hover: "rgba(15,23,42,0.04)" },
  dark: { bg: "#0F172A", bgS: "#111827", bgT: "#1E293B", text: "#E2E8F0", textS: "#94A3B8", textT: "#64748B", border: "#1E293B", accent: "#38BDF8", accentBg: "#0C4A6E", success: "#4ADE80", successBg: "#14352A", warning: "#FBBF24", warningBg: "#3A2E12", error: "#F87171", errorBg: "#3D2020", shadow: "0 1px 3px rgba(0,0,0,0.5)", hover: "rgba(255,255,255,0.04)" },
};

export interface Transaction { id: number; codigoFactura: string; cliente: string; fecha: string; total: number; tipoPago: string; estado: string; articulos: string[]; }

const getEstado = (estado: string, p: any) => {
  const s = estado.toLowerCase();
  if (s === "finalizada" || s === "pagada") return { label: "Pagada", bg: p.successBg, color: p.success, dot: p.success };
  if (s === "pendiente") return { label: "Pendiente", bg: p.warningBg, color: p.warning, dot: p.warning };
  if (s === "anulada" || s === "cancelada") return { label: "Anulada", bg: p.errorBg, color: p.error, dot: p.error };
  return { label: estado, bg: p.accentBg, color: p.accent, dot: p.accent };
};

const List = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedVenta, setSelectedVenta] = useState<Transaction | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const fetchData = useCallback(async () => {
    try { const data = await TransactionsServiceApi.getLatestTransactions(); setTransactions(data); }
    catch { } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { let m = true; const l = async () => { if (m) await fetchData(); }; l(); const iv = setInterval(l, 5 * 60 * 1000); return () => { m = false; clearInterval(iv); }; }, [fetchData]);

  const fmt = (v: number) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(v);
  const fmtDate = (d: string) => { try { return format(new Date(d), "d 'de' MMM, HH:mm", { locale: es }); } catch { return d; } };

  const thSx = { fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase" as const, letterSpacing: "0.04em", borderBottom: `1px solid ${p.border}`, bgcolor: p.bgS, py: 1.2 };
  const tdSx = { fontSize: "0.84rem", borderBottom: `1px solid ${p.border}`, py: 1.5, color: p.text };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress size={28} sx={{ color: p.accent }} /></Box>;
  if (!transactions.length) return (
    <Box sx={{ textAlign: "center", py: 6 }}>
      <Receipt sx={{ fontSize: 48, color: p.textT, mb: 1 }} />
      <Typography sx={{ fontSize: "0.88rem", color: p.textS }}>No hay transacciones recientes</Typography>
    </Box>
  );

  return (
    <Box sx={{ borderRadius: "12px", border: `1px solid ${p.border}`, bgcolor: p.bg, boxShadow: p.shadow, overflow: "hidden" }}>
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}` }}>
        <Box>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 650, color: p.text }}>Últimas Transacciones</Typography>
          <Typography sx={{ fontSize: "0.75rem", color: p.textT }}>{transactions.length} transacciones recientes</Typography>
        </Box>
        <Tooltip title="Actualizar"><IconButton onClick={() => { setRefreshing(true); fetchData(); }} sx={{ width: 30, height: 30, borderRadius: "7px", color: p.textS, "&:hover": { bgcolor: p.bgT } }}><Refresh sx={{ fontSize: 16, animation: refreshing ? "spin 1s linear infinite" : "none", "@keyframes spin": { from: { transform: "rotate(0)" }, to: { transform: "rotate(360deg)" } } }} /></IconButton></Tooltip>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>Factura</TableCell>
              <TableCell sx={thSx}>Artículos</TableCell>
              <TableCell sx={thSx}>Cliente</TableCell>
              <TableCell sx={thSx}>Fecha</TableCell>
              <TableCell align="right" sx={thSx}>Monto</TableCell>
              <TableCell sx={thSx}>Pago</TableCell>
              <TableCell sx={thSx}>Estado</TableCell>
              <TableCell align="center" sx={thSx}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((row) => {
              const est = getEstado(row.estado, p);
              return (
                <TableRow key={row.id} sx={{ "&:hover": { bgcolor: p.hover }, "&:last-child td": { borderBottom: "none" } }}>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: p.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{row.codigoFactura}</Typography>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Tooltip title={row.articulos.join(", ")}>
                      <Box sx={{ display: "inline-flex", px: 1, py: 0.2, borderRadius: "5px", bgcolor: p.bgT, fontSize: "0.75rem", fontWeight: 500, color: p.textS, cursor: "default" }}>
                        {row.articulos.length} artículo{row.articulos.length > 1 ? "s" : ""}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={tdSx}>{row.cliente}</TableCell>
                  <TableCell sx={{ ...tdSx, fontSize: "0.78rem", color: p.textS }}>{fmtDate(row.fecha)}</TableCell>
                  <TableCell align="right" sx={tdSx}>
                    <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(row.total)}</Typography>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Stack direction="row" alignItems="center" spacing={0.5}><Payment sx={{ fontSize: 14, color: p.textT }} /><Typography sx={{ fontSize: "0.78rem", color: p.textS }}>{row.tipoPago}</Typography></Stack>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.3, borderRadius: "6px", bgcolor: est.bg, color: est.color, fontSize: "0.75rem", fontWeight: 600 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: est.dot }} />{est.label}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={tdSx}>
                    <IconButton size="small" onClick={() => { setSelectedVenta(row); setDetailOpen(true); }} sx={{ width: 28, height: 28, borderRadius: "7px", color: p.accent, bgcolor: p.accentBg, "&:hover": { bgcolor: alpha(p.accent, 0.15) } }}>
                      <Visibility sx={{ fontSize: 15 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TransactionDetailModal transaction={selectedVenta} open={detailOpen} onClose={() => { setDetailOpen(false); setSelectedVenta(null); }} />
    </Box>
  );
};

export default List;
